import { useEffect, useState } from 'preact/hooks';

import { getSnapshot, subscribe } from './system-status-store';

// Stable reference so consumers listing this as an effect/memo dependency don't re-run
// every render while nothing has loaded yet.
const EMPTY = {};

/**
 * Subscribes the component to the shared `/api/systems/status` poll (see system-status-store)
 * and returns the full snapshot. Prefer `useSystemStatusPolling` / `useSystemStatus` unless you
 * need the loaded/error state.
 *
 * @returns {import('./system-status-store').StatusSnapshot}
 */
export const useSystemStatusSnapshot = () => {
    const [snapshot, setSnapshot] = useState(getSnapshot);

    useEffect(() => {
        // Pick up anything published between the render and this effect running.
        setSnapshot(getSnapshot());
        return subscribe(setSnapshot);
    }, []);

    return snapshot;
};

/**
 * Returns the latest system statuses, keyed by system name (e.g. `{ rdarr: { isActive },
 * ewis: { isActive } }`) - matches the shape `isOffline`/`GenerateHomeOptions` in
 * home.config.js expect. Empty until the first successful load. Shares one poll with every
 * other consumer rather than running its own.
 *
 * @returns {Record<string, { isActive: boolean }>}
 */
export const useSystemStatusPolling = () => useSystemStatusSnapshot().systems ?? EMPTY;
